import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space, message, Row, Col, Card, Statistic,
  Drawer, Descriptions, Switch, Divider, Typography, Alert, Spin, InputNumber,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, SearchOutlined,
  InfoCircleOutlined, SettingOutlined, BugOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { inverter_api } from '../api/inverter-api';

const { Text } = Typography;

const statusMap = {
  online: { color: 'green', text: 'Online' },
  offline: { color: 'default', text: 'Offline' },
  maintenance: { color: 'orange', text: 'Bảo trì' },
  error: { color: 'red', text: 'Lỗi' },
};

const relationMap = {
  owner: 'Chủ sở hữu',
  installer: 'Lắp đặt',
  operator: 'Vận hành',
  viewer: 'Xem',
};

function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString('vi-VN');
}

function warrantyTag(expiresAt) {
  if (!expiresAt) {
    return <Tag>Chưa có</Tag>;
  }
  const exp = new Date(expiresAt);
  const daysLeft = Math.ceil((exp.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysLeft < 0) {
    return <Tag color="red">Hết hạn ({formatDate(expiresAt)})</Tag>;
  }
  if (daysLeft <= 30) {
    return <Tag color="orange">Còn {daysLeft} ngày</Tag>;
  }
  return <Tag color="green">Còn {daysLeft} ngày</Tag>;
}

export default function Devices() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ status: undefined, search: '' });
  const [form] = Form.useForm();

  const [infoOpen, setInfoOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeDevice, setActiveDevice] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm] = Form.useForm();

  const rowId = (row) => row.id || row._id;

  const load = async () => {
    try {
      setLoading(true);
      const [listRes, statRes] = await Promise.all([
        inverter_api.getAllInverters({
          limit: 1000,
          status: filters.status,
          search: filters.search?.trim() || undefined,
        }),
        inverter_api.getInverterStatistics(),
      ]);
      if (listRes.success) setDevices(listRes.data || []);
      if (statRes.success) setStats(statRes.data || {});
    } catch (error) {
      message.error(error.message || 'Không tải được thiết bị');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      setDetailLoading(true);
      const res = await inverter_api.getInverterDetail(id);
      if (res.success) {
        setDetail(res.data);
        return res.data;
      }
      message.error(res.message || 'Không tải được chi tiết');
      return null;
    } catch (error) {
      message.error(error.message || 'Không tải được chi tiết');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const openInfo = async (record) => {
    setActiveDevice(record);
    setDetail(null);
    setInfoOpen(true);
    await loadDetail(rowId(record));
  };

  const openSettings = async (record) => {
    setActiveDevice(record);
    setDetail(null);
    setSettingsOpen(true);
    const data = await loadDetail(rowId(record));
    if (data) {
      settingsForm.setFieldsValue({
        name: data.name,
        status: data.status,
        debugMode: Boolean(data.debugMode),
        location: {
          latitude: data.location?.latitude ?? null,
          longitude: data.location?.longitude ?? null,
        },
      });
    } else {
      settingsForm.setFieldsValue({
        name: record.name,
        status: record.status,
        debugMode: Boolean(record.debugMode),
        location: {
          latitude: record.location?.latitude ?? null,
          longitude: record.location?.longitude ?? null,
        },
      });
    }
  };

  const saveSettings = async (values) => {
    if (!activeDevice) {
      return;
    }
    try {
      setSettingsSaving(true);
      const res = await inverter_api.updateInverterSettings(rowId(activeDevice), {
        name: values.name,
        status: values.status,
        debugMode: values.debugMode,
        location: values.location,
      });
      if (res.success) {
        message.success(res.message || 'Đã lưu cài đặt');
        setSettingsOpen(false);
        await load();
      } else {
        message.error(res.message || 'Lưu thất bại');
      }
    } catch (error) {
      message.error(error.message || 'Lưu thất bại');
    } finally {
      setSettingsSaving(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  const handleSubmit = async (values) => {
    const payload = {
      name: values.name,
      serialNumber: values.serialNumber,
      deviceId: values.deviceId,
      model: values.model || '',
      manufacturer: values.manufacturer || 'INVT',
      status: values.status || 'offline',
    };
    try {
      setLoading(true);
      const res = editing
        ? await inverter_api.updateInverter(rowId(editing), payload)
        : await inverter_api.createInverter(payload);
      if (res.success) {
        message.success(editing ? 'Đã cập nhật thiết bị' : 'Đã thêm và gán user — MQTTS tự động');
        setModalOpen(false);
        form.resetFields();
        setEditing(null);
        await load();
      } else {
        message.error(res.message || 'Thao tác thất bại');
      }
    } catch (error) {
      message.error(error.message || 'Thao tác thất bại');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'Tên', dataIndex: 'name', ellipsis: true },
    { title: 'Device ID', dataIndex: 'deviceId', width: 140 },
    { title: 'Serial', dataIndex: 'serialNumber', width: 140 },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 110,
      render: (status) => {
        const info = statusMap[status] || { color: 'default', text: status || 'N/A' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: 'Debug',
      width: 90,
      render: (_, row) => (
        row.debugMode
          ? <Tag color="purple">100ms</Tag>
          : <Tag>10s</Tag>
      ),
    },
    {
      title: 'Kết nối',
      width: 120,
      render: (_, row) => {
        const proto = row.connection?.protocol;
        const st = row.connection?.connectionStatus;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={st === 'connected' ? 'green' : 'default'}>{st || '—'}</Tag>
            {proto ? <span style={{ fontSize: 12, color: '#888' }}>{proto}</span> : null}
          </Space>
        );
      },
    },
    {
      title: 'PV / Load (W)',
      width: 140,
      render: (_, row) => {
        const pv = row.currentData?.pvPower ?? row.currentData?.powerOutput;
        const load = row.currentData?.loadPower;
        return `${pv ?? '—'} / ${load ?? '—'}`;
      },
    },
    {
      title: 'STM32',
      width: 80,
      render: (_, row) => {
        const linked = row.currentData?.stm32Link;
        if (linked == null) return '—';
        return linked ? <Tag color="green">OK</Tag> : <Tag color="red">Mất</Tag>;
      },
    },
    {
      title: 'Cập nhật',
      width: 160,
      render: (_, row) => {
        const time = row.currentData?.updatedAt || row.connection?.lastConnected || row.updated_at;
        return time ? new Date(time).toLocaleString('vi-VN') : '—';
      },
    },
    {
      title: '',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap size={0}>
          <Button type="link" icon={<InfoCircleOutlined />} onClick={() => openInfo(record)}>
            Info
          </Button>
          <Button
            type="link"
            icon={<BugOutlined />}
            onClick={() => navigate(`/devices/debug?id=${rowId(record)}`)}
          >
            Debug
          </Button>
          <Button type="link" icon={<SettingOutlined />} onClick={() => openSettings(record)}>
            Cài đặt
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              form.setFieldsValue({
                name: record.name,
                serialNumber: record.serialNumber,
                deviceId: record.deviceId,
                model: record.model,
                manufacturer: record.manufacturer,
                status: record.status,
              });
              setModalOpen(true);
            }}
          >
            Sửa
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: `Xóa ${record.name}?`,
                content:
                  'Sẽ xóa inverter, gỡ user khỏi máy và xóa mqtt_credentials. Stock về trạng thái sold.',
                onOk: async () => {
                  const res = await inverter_api.deleteInverter(rowId(record));
                  if (res.success) {
                    message.success(res.message || 'Đã xóa');
                    load();
                  } else {
                    message.error(res.message);
                  }
                },
              });
            }}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [form]);

  const detailData = detail || activeDevice;

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Thiết bị (ESP32 / Inverter)</h1>
        <Space wrap>
          <Input
            allowClear
            placeholder="Tìm tên / serial / deviceId"
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            onPressEnter={load}
          />
          <Select
            allowClear
            placeholder="Trạng thái"
            style={{ width: 140 }}
            value={filters.status}
            onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
            options={Object.entries(statusMap).map(([value, info]) => ({ value, label: info.text }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              form.resetFields();
              setModalOpen(true);
            }}
          >
            Thêm thiết bị
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={6}><Card><Statistic title="Tổng" value={stats.total || devices.length} prefix={<ThunderboltOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="Online" value={stats.online || 0} valueStyle={{ color: '#3f8600' }} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="Offline" value={stats.offline || 0} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="Lỗi" value={stats.error || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
      </Row>

      <Table
        rowKey={rowId}
        loading={loading}
        columns={columns}
        dataSource={devices}
        scroll={{ x: 1300 }}
        pagination={{ pageSize: 10, showTotal: (n) => `${n} thiết bị` }}
      />

      <Drawer
        title={detailData ? `Thông tin — ${detailData.name}` : 'Thông tin thiết bị'}
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        width={560}
      >
        {detailLoading ? (
          <Spin />
        ) : detailData ? (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Device ID">{detailData.deviceId}</Descriptions.Item>
              <Descriptions.Item label="Serial">{detailData.serialNumber}</Descriptions.Item>
              <Descriptions.Item label="Model">{detailData.model || '—'}</Descriptions.Item>
              <Descriptions.Item label="Hãng">{detailData.manufacturer || '—'}</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={statusMap[detailData.status]?.color || 'default'}>
                  {statusMap[detailData.status]?.text || detailData.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Debug MQTT">
                {detailData.debugMode ? <Tag color="purple">Bật (100ms)</Tag> : <Tag>Tắt (10s)</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Vị trí (GPS)">
                {detailData.location?.latitude != null && detailData.location?.longitude != null
                  ? `${detailData.location.latitude}, ${detailData.location.longitude}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày kích hoạt">{formatDate(detailData.activatedAt)}</Descriptions.Item>
              <Descriptions.Item label="Bảo hành">
                {detailData.warrantyMonths != null ? `${detailData.warrantyMonths} tháng` : '—'}
                {' '}
                {warrantyTag(detailData.warrantyExpiresAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Hết hạn BH">{formatDate(detailData.warrantyExpiresAt)}</Descriptions.Item>
              <Descriptions.Item label="Tạo lúc">{formatDate(detailData.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Cập nhật">{formatDate(detailData.updated_at)}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Người dùng</Divider>
            {(detail?.owners || []).length === 0 ? (
              <Text type="secondary">Chưa gán user</Text>
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={detail.owners}
                columns={[
                  { title: 'Email', dataIndex: 'email', ellipsis: true },
                  { title: 'Tên', dataIndex: 'displayName', render: (v) => v || '—' },
                  {
                    title: 'Vai trò',
                    dataIndex: 'relation',
                    render: (v) => relationMap[v] || v,
                  },
                  {
                    title: 'Gán lúc',
                    dataIndex: 'grantedAt',
                    render: formatDate,
                  },
                ]}
              />
            )}

            <Divider orientation="left">Dự án & Stock</Divider>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Dự án">
                {detail?.project?.name || '—'}
                {detail?.project?.id ? ` (#${detail.project.id})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Stock batch">{detail?.stock?.batch || '—'}</Descriptions.Item>
              <Descriptions.Item label="Stock status">{detail?.stock?.status || '—'}</Descriptions.Item>
              <Descriptions.Item label="Xuất xưởng">{formatDate(detail?.stock?.manufacturedAt)}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Kết nối & Telemetry</Divider>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Protocol">{detailData.connection?.protocol || '—'}</Descriptions.Item>
              <Descriptions.Item label="Connection">{detailData.connection?.connectionStatus || '—'}</Descriptions.Item>
              <Descriptions.Item label="Last connected">{formatDate(detailData.connection?.lastConnected)}</Descriptions.Item>
              <Descriptions.Item label="STM32 link">
                {detailData.currentData?.stm32Link == null
                  ? '—'
                  : (detailData.currentData.stm32Link ? 'OK' : 'Mất')}
              </Descriptions.Item>
              <Descriptions.Item label="Wi‑Fi RSSI">
                {detailData.currentData?.wifiRssi != null ? `${detailData.currentData.wifiRssi} dBm` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="ESP temp">
                {detailData.currentData?.espTemperature != null
                  ? `${detailData.currentData.espTemperature} °C`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="MQTT creds">
                {detail?.mqtt?.hasCredentials ? `Có (hint …${detail.mqtt.hint || '?'})` : 'Không'}
              </Descriptions.Item>
              <Descriptions.Item label="Data cập nhật">
                {formatDate(detailData.currentData?.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : null}
      </Drawer>

      <Drawer
        title={activeDevice ? `Cài đặt — ${activeDevice.name}` : 'Cài đặt thiết bị'}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        width={420}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Debug mode gửi MQTT mỗi 100ms. Chỉ bật khi cần debug 1 thiết bị; ESP phải online để nhận lệnh."
        />
        <Form form={settingsForm} layout="vertical" onFinish={saveSettings}>
          <Form.Item name="name" label="Tên hiển thị" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Trạng thái hệ thống">
            <Select options={Object.entries(statusMap).map(([value, info]) => ({ value, label: info.text }))} />
          </Form.Item>
          <Form.Item
            name="debugMode"
            label="Debug MQTT (100ms)"
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>

          <Divider orientation="left" plain>Vị trí (thời tiết)</Divider>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Tọa độ GPS dùng lấy thời tiết cho thiết bị. Để trống cả hai ô nếu chưa xác định."
          />
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Vĩ độ"
                name={['location', 'latitude']}
                rules={[
                  {
                    validator: (_, value) => {
                      const lng = settingsForm.getFieldValue(['location', 'longitude']);
                      const latEmpty = value === undefined || value === null || value === '';
                      const lngEmpty = lng === undefined || lng === null || lng === '';
                      if (latEmpty && lngEmpty) return Promise.resolve();
                      if (latEmpty) return Promise.reject('Nhập latitude hoặc xoá cả hai');
                      const num = Number(value);
                      if (Number.isNaN(num) || num < -90 || num > 90) {
                        return Promise.reject('Latitude trong [-90, 90]');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="16.0544" step={0.000001} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Kinh độ"
                name={['location', 'longitude']}
                rules={[
                  {
                    validator: (_, value) => {
                      const lat = settingsForm.getFieldValue(['location', 'latitude']);
                      const latEmpty = lat === undefined || lat === null || lat === '';
                      const lngEmpty = value === undefined || value === null || value === '';
                      if (latEmpty && lngEmpty) return Promise.resolve();
                      if (lngEmpty) return Promise.reject('Nhập longitude hoặc xoá cả hai');
                      const num = Number(value);
                      if (Number.isNaN(num) || num < -180 || num > 180) {
                        return Promise.reject('Longitude trong [-180, 180]');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="108.2022" step={0.000001} />
              </Form.Item>
            </Col>
          </Row>

          <Button type="primary" htmlType="submit" loading={settingsSaving} block>
            Lưu cài đặt
          </Button>
        </Form>
      </Drawer>

      <Modal
        title={editing ? 'Sửa thiết bị' : 'Thêm thiết bị'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Tên" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="deviceId"
            label="Device ID (MQTT)"
            rules={[{ required: true }, { max: 23, message: 'Tối đa 23 ký tự' }]}
          >
            <Input placeholder="HYA-18EB3DEC" disabled={Boolean(editing)} maxLength={23} />
          </Form.Item>
          <Form.Item name="serialNumber" label="Serial" rules={[{ required: true }]}>
            <Input disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item name="model" label="Model"><Input /></Form.Item>
          <Form.Item name="manufacturer" label="Hãng"><Input /></Form.Item>
          <Form.Item name="status" label="Trạng thái" initialValue="offline">
            <Select options={Object.entries(statusMap).map(([value, info]) => ({ value, label: info.text }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            {editing ? 'Cập nhật' : 'Tạo'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
